/**
 * Clarity visitor session id — first-party cookie with Max-Age.
 * Reused for anonymous customId and always sent as Clarity custom-session-id + tag.
 */

export const CLARITY_SESSION_COOKIE_NAME = "kivu_c_sid";

/** Default: 1 year — keep anonymous journeys stitchable across visits. */
const DEFAULT_MAX_AGE_SECONDS = 31536000;
const MIN_MAX_AGE_SECONDS = 86400;
const MAX_MAX_AGE_SECONDS = 63072000; // 2 years

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function safeGetCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  try {
    const prefix = `${name}=`;
    const parts = document.cookie.split(";");
    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length));
      }
    }
    return null;
  } catch {
    return null;
  }
}

function isSecureCookieContext(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.protocol === "https:";
}

export function getClaritySessionMaxAgeSeconds(): number {
  const raw = process.env.REACT_APP_CLARITY_SESSION_MAX_AGE_SECONDS?.trim();
  if (!raw) {
    return DEFAULT_MAX_AGE_SECONDS;
  }
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds)) {
    return DEFAULT_MAX_AGE_SECONDS;
  }
  return Math.min(MAX_MAX_AGE_SECONDS, Math.max(MIN_MAX_AGE_SECONDS, seconds));
}

export function isValidClaritySessionId(
  value: string | null | undefined
): boolean {
  if (!value) {
    return false;
  }
  return UUID_V4_PATTERN.test(value.trim());
}

export function getClaritySessionIdFromCookie(): string | null {
  const value = safeGetCookie(CLARITY_SESSION_COOKIE_NAME);
  return isValidClaritySessionId(value) ? value!.trim() : null;
}

export function setClaritySessionCookie(sessionId: string): void {
  if (typeof document === "undefined" || !isValidClaritySessionId(sessionId)) {
    return;
  }
  const maxAge = getClaritySessionMaxAgeSeconds();
  const secure = isSecureCookieContext() ? "; Secure" : "";
  document.cookie = `${CLARITY_SESSION_COOKIE_NAME}=${encodeURIComponent(
    sessionId.trim()
  )}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

/** Read cookie or create a new RFC4122 v4 id and persist it. */
export function getOrCreateClaritySessionId(): string {
  const existing = getClaritySessionIdFromCookie();
  if (existing) {
    return existing;
  }
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : fallbackUuidV4();
  setClaritySessionCookie(id);
  return id;
}

function fallbackUuidV4(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    ""
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
